import React, { useState, useRef, useEffect } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Menu, X, User, ChevronDown, LogOut, LayoutDashboard } from "lucide-react";
import { useLanguage } from "../contexts/LanguageContext";
import { useAuth } from "../contexts/AuthContext";

const SCROLL_THRESHOLD = 20;
const DISSOLVE_MS = 180;
const HERO_SECTION_MAX_Y = 420;
const DRIFT_TOP_PX = 16;        // final gap size (1rem)
const DRIFT_SCROLL_RANGE = 220; // scroll distance over which nav drifts from 0 to DRIFT_TOP_PX
const MOBILE_BREAKPOINT = 768;  // md

const Navbar = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [servicesDropdownOpen, setServicesDropdownOpen] = useState(false);
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(() => typeof window !== 'undefined' && window.innerWidth < MOBILE_BREAKPOINT);
  const [scrollY, setScrollY] = useState(() => typeof window !== 'undefined' ? window.scrollY : 0);
  const [scrolled, setScrolled] = useState(() => typeof window !== 'undefined' && window.scrollY > SCROLL_THRESHOLD);
  const [displayMode, setDisplayMode] = useState(() => typeof window !== 'undefined' && window.scrollY > SCROLL_THRESHOLD ? 'floating' : 'top');
  const transitionRef = useRef(null);
  const dropdownRef = useRef(null);
  const userMenuRef = useRef(null);
  const location = useLocation();
  const navigate = useNavigate();
  const { language, toggleLanguage, t } = useLanguage();
  const { user, isAuthenticated, isDevMode, logout } = useAuth();

  useEffect(() => {
    const check = () => setIsMobile(window.innerWidth < MOBILE_BREAKPOINT);
    window.addEventListener('resize', check);
    check();
    return () => window.removeEventListener('resize', check);
  }, []);

  useEffect(() => {
    if (!userMenuOpen) return;
    const handleClick = (e) => {
      if (userMenuRef.current && !userMenuRef.current.contains(e.target)) setUserMenuOpen(false);
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [userMenuOpen]);

  const sectionLinks = [
    { name: t("nav.about"), hash: "#about", key: "about" },
    { name: t("nav.joinUs"), path: "/careers", key: "joinUs" },
  ];

  const servicesDropdownLinks = [
    { name: t("nav.sillyDragon"), path: "/silly-dragon", key: "sillyDragon" },
    { name: t("nav.agentBi"), path: "/agent-bi", key: "agentBi" },
    { name: t("nav.education"), path: "/education", key: "education" },
    { name: t("nav.forum"), path: "/info", key: "forum" },
    { name: t("nav.community"), path: "/aid", key: "community" },
    { name: t("nav.moreToCome"), path: null, key: "moreToCome", disabled: true },
  ];

  useEffect(() => {
    const onScroll = () => {
      const y = window.scrollY;
      setScrollY(y);
      setScrolled(y > SCROLL_THRESHOLD);
    };
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  // Delayed switch between top and floating so position/background transition is smooth
  useEffect(() => {
    if (transitionRef.current) clearTimeout(transitionRef.current);
    if (scrolled && displayMode === 'top') {
      transitionRef.current = setTimeout(() => {
        setDisplayMode('floating');
        transitionRef.current = null;
      }, DISSOLVE_MS);
    } else if (!scrolled && displayMode === 'floating') {
      transitionRef.current = setTimeout(() => {
        setDisplayMode('top');
        transitionRef.current = null;
      }, DISSOLVE_MS);
    }
    return () => {
      if (transitionRef.current) clearTimeout(transitionRef.current);
    };
  }, [scrolled, displayMode]);

  // On mobile: always solid bar at top, no transparent/floating or drift animation
  const transparent = !isMobile && displayMode === 'top';
  const isHome = location.pathname === "/";
  const gapUseHeroStyle = isHome && scrollY < HERO_SECTION_MAX_Y;
  const driftTopPx = isMobile ? 0 : (!transparent
    ? Math.min(DRIFT_TOP_PX, Math.max(0, (scrollY - SCROLL_THRESHOLD) * (DRIFT_TOP_PX / DRIFT_SCROLL_RANGE)))
    : 0);
  const borderOpacity = transparent ? 0 : (driftTopPx / DRIFT_TOP_PX) * 0.08;
  // At top of home page, don't highlight any nav item (avoids "About" selected after hard refresh with #about in URL)
  const isAtTop = isHome && scrollY <= 80;
  const isNavLinkActive = (hash) => location.hash === hash && !isAtTop;

  const scrollToSection = (hash) => {
    if (!hash) return;
    if (location.pathname !== "/") {
      window.location.href = `/${hash}`;
      return;
    }
    const el = document.querySelector(hash);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: "smooth" });
    if (location.pathname === "/" && location.hash) {
      navigate("/", { replace: true });
    }
  };

  return (
    <>
      {!transparent && driftTopPx > 0 && gapUseHeroStyle && (
        <div
          className="fixed top-0 left-0 right-0 z-[50] pointer-events-none bg-dark overflow-hidden"
          style={{ height: `${driftTopPx}px` }}
          aria-hidden="true"
        >
          <div className="absolute inset-0 bg-gradient-mesh" />
          <div className="absolute inset-0 bg-dot-pattern bg-dot opacity-50" />
          <div className="absolute inset-0 bg-gradient-to-b from-transparent via-blue-600/[0.04] to-blue-600/[0.06]" />
        </div>
      )}
      {!transparent && driftTopPx > 0 && !gapUseHeroStyle && (
        <div
          className="fixed top-0 left-0 right-0 z-[50] pointer-events-none bg-dark bg-starfield bg-[length:400px_400px]"
          style={{ height: `${driftTopPx}px` }}
          aria-hidden="true"
        />
      )}
      <nav
        className={`fixed left-3 right-3 sm:left-4 sm:right-4 md:left-1/2 md:right-auto md:-translate-x-1/2 md:w-full md:max-w-[88rem] z-[60] rounded-2xl opacity-100 border transition-[background-color,backdrop-filter,box-shadow,border-color] duration-300 ease-out ${
          transparent
            ? "top-0 bg-transparent backdrop-blur-none shadow-none"
            : "bg-dark-card/90 md:bg-dark-card/40 md:backdrop-blur-2xl shadow-xl shadow-black/20"
        }`}
        style={{
          top: transparent ? 0 : driftTopPx,
          borderColor: `rgba(255, 255, 255, ${borderOpacity})`,
        }}
      >
      <div className="max-w-[88rem] mx-auto px-4 sm:px-6 lg:px-8">
        <div className="relative flex justify-between items-center min-h-[4.5rem] md:min-h-[5rem]">
          <div className="flex items-center flex-shrink-0 z-10">
            <Link
              to="/"
              className="flex items-center min-w-0 group"
              onClick={(e) => {
                if (isHome) {
                  e.preventDefault();
                  scrollToTop();
                }
              }}
            >
              {language === "zh" ? (
                <>
                  <span className="font-bold text-xl sm:text-2xl text-gradient-animated-yellow tracking-tight transition-all duration-300">
                    华人社区AI
                  </span>
                  <span className="text-[11px] font-medium ml-2.5 px-2.5 py-1 rounded-full bg-white/10 text-gray-400 border border-white/5 hidden sm:block">
                    CCAI
                  </span>
                </>
              ) : (
                <>
                  <span className="font-bold text-xl sm:text-2xl text-gradient-animated-yellow tracking-tight transition-all duration-300">
                    CCAI
                  </span>
                  <span className="text-[11px] font-medium ml-2.5 px-2.5 py-1 rounded-full bg-white/10 text-gray-400 border border-white/5 hidden sm:block">
                    华人社区AI
                  </span>
                </>
              )}
            </Link>
          </div>
          {/* Mobile: Projects, About, Join Us on the bar */}
          <div className="flex md:hidden items-center gap-2 sm:gap-3 flex-1 justify-center min-w-0">
            <Link
              to="/#projects"
              onClick={(e) => {
                if (isHome) {
                  e.preventDefault();
                  scrollToSection("#projects");
                }
                setIsOpen(false);
              }}
              className={`inline-flex items-center min-h-[44px] px-3 sm:px-4 py-2.5 rounded-lg text-sm sm:text-base font-bold text-white transition-all duration-200 whitespace-nowrap touch-manipulation ${
                isNavLinkActive("#projects") ? "bg-white/10" : "hover:bg-white/[0.06] active:bg-white/10"
              }`}
            >
              {t("nav.services")}
            </Link>
            {sectionLinks.map((link) =>
              link.path ? (
                <Link
                  key={link.key}
                  to={link.path}
                  onClick={() => setIsOpen(false)}
                  className={`inline-flex items-center min-h-[44px] px-3 sm:px-4 py-2.5 rounded-lg text-sm sm:text-base font-bold text-white transition-all duration-200 whitespace-nowrap touch-manipulation ${
                    location.pathname === link.path ? "bg-white/10" : "hover:bg-white/[0.06] active:bg-white/10"
                  }`}
                >
                  {link.name}
                </Link>
              ) : (
                <Link
                  key={link.key}
                  to={`/${link.hash}`}
                  onClick={(e) => {
                    e.preventDefault();
                    scrollToSection(link.hash);
                    setIsOpen(false);
                  }}
                  className={`inline-flex items-center min-h-[44px] px-3 sm:px-4 py-2.5 rounded-lg text-sm sm:text-base font-bold text-white transition-all duration-200 whitespace-nowrap touch-manipulation ${
                    isNavLinkActive(link.hash) ? "bg-white/10" : "hover:bg-white/[0.06] active:bg-white/10"
                  }`}
                >
                  {link.name}
                </Link>
              )
            )}
          </div>
          <div className="hidden md:flex absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 items-center justify-center gap-3">
              {/* Projects: click goes to #projects, hover shows dropdown */}
              <div
                className="relative"
                ref={dropdownRef}
                onMouseEnter={() => setServicesDropdownOpen(true)}
                onMouseLeave={() => setServicesDropdownOpen(false)}
              >
                <Link
                  to="/#projects"
                  onClick={(e) => {
                    if (isHome) {
                      e.preventDefault();
                      scrollToSection("#projects");
                    }
                  }}
                  className={`relative min-w-[7.5rem] text-center px-5 py-2.5 rounded-lg text-base font-bold text-white transition-all duration-300 inline-flex items-center justify-center gap-1.5 ${
                    isNavLinkActive("#projects")
                      ? "bg-white/10"
                      : "hover:bg-white/[0.06]"
                  }`}
                >
                  {t("nav.services")}
                  <ChevronDown
                    className={`h-4 w-4 transition-transform duration-300 ${
                      servicesDropdownOpen ? "rotate-180" : ""
                    }`}
                  />
                </Link>
                {servicesDropdownOpen && (
                  <div className="absolute left-0 top-full pt-2">
                    <div className="w-52 bg-dark-card/95 backdrop-blur-2xl rounded-xl border border-dark-border/60 py-1.5 z-50 animate-scale-in shadow-dark-glow overflow-hidden">
                    {servicesDropdownLinks.map((link) =>
                      link.disabled ? (
                        <span
                          key={link.key}
                          className="block px-4 py-2.5 text-base text-gray-500 cursor-not-allowed"
                        >
                          {link.name}
                        </span>
                      ) : (
                        <Link
                          key={link.key}
                          to={link.path}
                          className="block px-4 py-2.5 text-base text-gray-300 hover:bg-white/[0.06] hover:text-white transition-all duration-200"
                        >
                          {link.name}
                        </Link>
                      )
                    )}
                    </div>
                  </div>
                )}
              </div>
              {sectionLinks.map((link) =>
                link.path ? (
                  <Link
                    key={link.key}
                    to={link.path}
                    className={`relative min-w-[7.5rem] text-center px-5 py-2.5 rounded-lg text-base font-bold text-white transition-all duration-300 ${
                      location.pathname === link.path
                        ? "bg-white/10"
                        : "hover:bg-white/[0.06]"
                    }`}
                  >
                    {link.name}
                    {location.pathname === link.path && (
                      <span className="absolute bottom-0 left-1/2 -translate-x-1/2 w-4 h-[2px] rounded-full bg-accent" />
                    )}
                  </Link>
                ) : (
                  <Link
                    key={link.key}
                    to={`/${link.hash}`}
                    onClick={(e) => {
                      e.preventDefault();
                      scrollToSection(link.hash);
                    }}
                    className={`relative min-w-[7.5rem] text-center px-5 py-2.5 rounded-lg text-base font-bold text-white transition-all duration-300 ${
                      isNavLinkActive(link.hash)
                        ? "bg-white/10"
                        : "hover:bg-white/[0.06]"
                    }`}
                  >
                    {link.name}
                    {isNavLinkActive(link.hash) && (
                      <span className="absolute bottom-0 left-1/2 -translate-x-1/2 w-4 h-[2px] rounded-full bg-accent" />
                    )}
                  </Link>
                )
              )}
          </div>
          <div className="hidden md:flex items-center gap-2 flex-shrink-0 z-10">
            <button
              onClick={toggleLanguage}
              className="px-4 py-2 rounded-lg text-[14px] font-bold text-white transition-all duration-300 hover:bg-white/[0.06]"
              title={language === "zh" ? t("common.langSwitchEn") : t("common.langSwitchZh")}
            >
              {language === "zh" ? "EN" : "中文"}
            </button>
            {isAuthenticated ? (
              <div className="relative" ref={userMenuRef}>
                <button
                  onClick={() => setUserMenuOpen(!userMenuOpen)}
                  className="flex items-center gap-2 px-3 py-2 rounded-full text-[14px] font-bold text-white hover:bg-white/[0.15] border border-white/[0.08] transition-all duration-300"
                >
                  <span className="w-6 h-6 rounded-full bg-accent flex items-center justify-center text-[11px] font-bold text-white uppercase">
                    {(user?.username || 'U')[0]}
                  </span>
                  <span className="max-w-[100px] truncate">{user?.username}</span>
                  {isDevMode && <span className="text-[9px] px-1.5 py-0.5 rounded bg-yellow-500/20 text-yellow-400 font-medium">DEV</span>}
                  <ChevronDown className={`h-3.5 w-3.5 transition-transform duration-200 ${userMenuOpen ? 'rotate-180' : ''}`} />
                </button>
                {userMenuOpen && (
                  <div className="absolute right-0 top-full mt-2 w-48 bg-dark-card/95 backdrop-blur-2xl rounded-xl border border-dark-border/60 py-1.5 shadow-dark-glow animate-scale-in overflow-hidden z-50">
                    <Link
                      to="/dashboard"
                      onClick={() => setUserMenuOpen(false)}
                      className="flex items-center gap-2.5 px-4 py-2.5 text-sm text-gray-300 hover:bg-white/[0.06] hover:text-white transition-all duration-200"
                    >
                      <LayoutDashboard className="h-4 w-4" />
                      {t('nav.dashboard') || 'Dashboard'}
                    </Link>
                    <button
                      onClick={() => { logout(); setUserMenuOpen(false); }}
                      className="flex items-center gap-2.5 w-full px-4 py-2.5 text-sm text-gray-300 hover:bg-white/[0.06] hover:text-white transition-all duration-200"
                    >
                      <LogOut className="h-4 w-4" />
                      {t('nav.logout') || 'Log Out'}
                    </button>
                  </div>
                )}
              </div>
            ) : (
              <Link
                to="/login"
                className="flex items-center gap-2 px-4 py-2 rounded-full text-[14px] font-bold text-white hover:bg-white/[0.15] border border-white/[0.08] transition-all duration-300"
              >
                <User className="h-3.5 w-3.5" />
                {t("nav.login")}
              </Link>
            )}
          </div>
          <div className="flex items-center md:hidden">
            <button
              onClick={() => setIsOpen(!isOpen)}
              className="inline-flex items-center justify-center min-w-[44px] min-h-[44px] p-2 rounded-lg transition-all duration-200 text-gray-300 hover:text-white hover:bg-white/10 active:bg-white/15"
              aria-expanded={isOpen}
              aria-label={isOpen ? t("nav.closeMenu") : t("nav.openMenu")}
            >
              {isOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
            </button>
          </div>
        </div>
      </div>

      {isOpen && (
        <div className="md:hidden bg-dark-card border-t border-dark-border/40 animate-fade-in">
          <div className="px-4 pt-3 pb-4">
            <div className="px-4 pt-1 pb-1">
              <div className="text-gray-500 text-[10px] font-semibold uppercase tracking-widest mb-2">
                {t("nav.services")}
              </div>
              {servicesDropdownLinks.map((link) =>
                link.disabled ? (
                  <span key={link.key} className="text-gray-500 block py-2 text-sm">{link.name}</span>
                ) : (
                  <Link key={link.key} to={link.path} className="text-gray-300 hover:text-white block py-2 text-sm transition-colors" onClick={() => setIsOpen(false)}>
                    {link.name}
                  </Link>
                )
              )}
            </div>
            <div className="flex items-center gap-3 px-4 pt-4 border-t border-dark-border/30 mt-3">
              <button onClick={() => { toggleLanguage(); setIsOpen(false); }}
                className="text-white font-bold hover:opacity-90 text-sm transition-colors">
                {language === "zh" ? "EN" : "中文"}
              </button>
              {isAuthenticated ? (
                <>
                  <Link to="/dashboard" className="flex items-center gap-1.5 text-white font-bold hover:opacity-90 text-sm transition-colors" onClick={() => setIsOpen(false)}>
                    <LayoutDashboard className="h-4 w-4" />{t('nav.dashboard') || 'Dashboard'}
                  </Link>
                  <button onClick={() => { logout(); setIsOpen(false); }} className="flex items-center gap-1.5 text-gray-400 font-bold hover:text-white text-sm transition-colors">
                    <LogOut className="h-4 w-4" />{t('nav.logout') || 'Log Out'}
                  </button>
                </>
              ) : (
                <Link to="/login" className="flex items-center gap-1.5 text-white font-bold hover:opacity-90 text-sm transition-colors" onClick={() => setIsOpen(false)}>
                  <User className="h-4 w-4" />{t("nav.login")}
                </Link>
              )}
            </div>
          </div>
        </div>
      )}
    </nav>
    </>
  );
};

export default Navbar;
